import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { LoginPage } from './pages/LoginPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { SysAdminDashboard } from './pages/admin/SysAdminDashboard';
import { UsersPage } from './pages/admin/UsersPage';
import { ClientsPage } from './pages/admin/ClientsPage';
import { FinancePage } from './pages/admin/FinancePage';
import { ProjectsPage } from './pages/projects/ProjectsPage';
import { ETLPage } from './pages/etl/ETLPage';
import { ChangeOrdersPage } from './pages/changes/ChangeOrdersPage';
import { AdvancesPage } from './pages/admin/AdvancesPage';
import { DocumentsPage } from './pages/admin/DocumentsPage';
import { TokensAdminDashboard } from './pages/admin/TokensAdminDashboard';
import { TokenPackagesPage } from './pages/admin/TokenPackagesPage';
import { AIServicesPage } from './pages/admin/AIServicesPage';
import { TokenTransactionsPage } from './pages/admin/TokenTransactionsPage';
import { MENU_ITEMS } from './config/role-routes';
import { ManualCapturePage } from './pages/manual-capture/ManualCapturePage';
import { InternalPage } from './pages/internal/InternalPage';
import { CatalogsPage } from './pages/admin/CatalogsPage';
import { BalancesPage } from './pages/balances/BalancesPage';
import { ReceiptApprovalPage } from './pages/admin/ReceiptApprovalPage';
import { MessagesPage } from './pages/admin/MessagesPage';
import { LegacyImportPage } from './pages/etl/LegacyImportPage';


interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
}

function getRolesForPath(path: string): string[] {
  const item = MENU_ITEMS.find(i => i.path === path);
  return item?.roles || [];
}

function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando sesión...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Verificar rol si se especificaron roles permitidos
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return (
      <DashboardLayout title="Acceso Denegado">
        <div className="text-center py-16">
          <div className="text-6xl mb-4">🚫</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Acceso Denegado
          </h2>
          <p className="text-gray-600 mb-6">
            Tu rol ({user.role}) no tiene permisos para acceder a esta sección.
          </p>
          <a
            href="/admin"
            className="inline-block px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            Volver al Dashboard
          </a>
        </div>
      </DashboardLayout>
    );
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/" element={<Navigate to="/admin" replace />} />

      <Route path="/admin" element={
       <ProtectedRoute allowedRoles={getRolesForPath('/admin')}>
          <DashboardLayout title="Dashboard">
            <SysAdminDashboard />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/receipts" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/admin/receipts')}>
            <DashboardLayout title="Comprobantes Pendientes">
            <ReceiptApprovalPage />
            </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/users" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/users')}>
          <DashboardLayout title="Gestión de Personal">
            <UsersPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/clients" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/clients')}>
          <DashboardLayout title="Clientes">
            <ClientsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/finance" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/finance')}>
          <DashboardLayout title="Finanzas">
            <FinancePage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/projects')}>
          <DashboardLayout title="Proyectos">
            <ProjectsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/etl" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/etl')}>
          <DashboardLayout title="Ingesta de Datos">
            <ETLPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/legacy-import" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/legacy-import')}>
            <DashboardLayout title="Migración Legacy">
            <LegacyImportPage />
            </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/changes/:projectId" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/changes/:projectId')}>
          <DashboardLayout title="Órdenes de Cambio">
            <ChangeOrdersPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/advances" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/advances')}>
          <DashboardLayout title="Avances de Obra">
            <AdvancesPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/documents" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/documents')}>
          <DashboardLayout title="Documentos">
            <DocumentsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Rutas de Administración de Tokens */}
      <Route path="/admin/tokens" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/admin/tokens')}>
          <DashboardLayout title="Administración de Tokens">
            <TokensAdminDashboard />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/tokens/packages" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/admin/tokens/packages')}>
          <DashboardLayout title="Paquetes de Tokens">
            <TokenPackagesPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/tokens/ai-services" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/admin/tokens/ai-services')}>
          <DashboardLayout title="Servicios de IA">
            <AIServicesPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/tokens/transactions" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/admin/tokens/transactions')}>
          <DashboardLayout title="Transacciones de Tokens">
            <TokenTransactionsPage />
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/manual-capture" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/manual-capture')}>
            <DashboardLayout title="Captura Manual">
            <ManualCapturePage />
            </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/internal" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/internal')}>
            <DashboardLayout title="Interno">
            <InternalPage />
            </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/admin/catalogs" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/admin/catalogs')}>
            <DashboardLayout title="Catálogos">
            <CatalogsPage />
            </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/balances" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/balances')}>
            <DashboardLayout title="Saldos">
            <BalancesPage />
            </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/messages" element={
        <ProtectedRoute allowedRoles={getRolesForPath('/messages')}>
            <DashboardLayout title="Mensajes">
            <MessagesPage />
            </DashboardLayout>
        </ProtectedRoute>
      } />


    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;